import { Card, CardHeader } from '@/components/ui';
import { formatRelativeTime } from '@/pages/activity/utils';
import type { Integration } from '@/types/integrations';
import type { GoogleWatch } from '@/types/google';

/**
 * Statistics card mirroring the integration detail page, aggregated across the
 * org's Google connections (Gmail + Chat). Counts are connection-wide totals,
 * not per-space. Shared by the space connect page and the org-admin page.
 */
export default function GoogleConnectionStats({
  connections,
}: {
  connections: Integration[];
}) {
  if (connections.length === 0) return null;
  const totalEvents = connections.reduce((s, c) => s + (c.eventCount ?? 0), 0);
  const errorCount = connections.reduce((s, c) => s + (c.errorCount ?? 0), 0);
  const lastEventAt = connections
    .map((c) => c.lastEventAt)
    .filter((v): v is string => !!v)
    .sort()
    .at(-1);
  const createdAt = connections
    .map((c) => c.createdAt)
    .filter((v): v is string => !!v)
    .sort()[0];

  return (
    <Card>
      <CardHeader title="Statistics" />
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Total Events</dt>
          <dd className="mt-1 text-2xl font-semibold text-foreground">
            {totalEvents}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Errors</dt>
          <dd
            className={`mt-1 text-2xl font-semibold ${
              errorCount > 0
                ? 'text-red-600 dark:text-red-400'
                : 'text-foreground'
            }`}
          >
            {errorCount}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Last Event</dt>
          <dd className="mt-1 text-sm text-foreground">
            {lastEventAt ? formatRelativeTime(lastEventAt) : 'Never'}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Created</dt>
          <dd className="mt-1 text-sm text-foreground">
            {createdAt ? new Date(createdAt).toLocaleString() : '—'}
          </dd>
        </div>
      </dl>
    </Card>
  );
}

/**
 * Space-scoped Google stats, derived from the space's watches (each watch
 * routes to exactly one space, so summing across them is per-space — unlike the
 * org-wide connection counters in `GoogleConnectionStats`). Shown on the
 * per-space connect page so a space with no subscriptions reads 0, not the org
 * totals.
 */
export function GoogleSpaceStats({ watches }: { watches: GoogleWatch[] }) {
  const active = watches.filter((w) => w.status === 'active').length;
  const totalEvents = watches.reduce((s, w) => s + (w.eventCount ?? 0), 0);
  const errorCount = watches.reduce((s, w) => s + (w.errorCount ?? 0), 0);
  const lastEventAt = watches
    .map((w) => w.lastEventAt)
    .filter((v): v is string => !!v)
    .sort()
    .at(-1);

  return (
    <Card>
      <CardHeader title="Statistics" description="For this space only." />
      <dl className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Subscriptions</dt>
          <dd className="mt-1 text-2xl font-semibold text-foreground">{active}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Total Events</dt>
          <dd className="mt-1 text-2xl font-semibold text-foreground">{totalEvents}</dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Errors</dt>
          <dd
            className={`mt-1 text-2xl font-semibold ${
              errorCount > 0 ? 'text-red-600 dark:text-red-400' : 'text-foreground'
            }`}
          >
            {errorCount}
          </dd>
        </div>
        <div>
          <dt className="text-sm font-medium text-muted-foreground">Last Event</dt>
          <dd className="mt-1 text-sm text-foreground">
            {lastEventAt ? formatRelativeTime(lastEventAt) : 'Never'}
          </dd>
        </div>
      </dl>
    </Card>
  );
}
